import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../api/client';
import type { DraftPort } from './editor-types';

/**
 * Enregistrement du brouillon d'un studio au serveur (ADR-146 addendum (11)), comme Wix ou Webflow :
 * automatique après un court délai sans modification, ou tout de suite (« Enregistrer », Ctrl S). Un seul
 * enregistrement à la fois ; la version du brouillon sert la concurrence optimiste. Rien n'est publié.
 */

/** Délai sans modification avant l'enregistrement automatique. */
export const AUTOSAVE_MS = 1500;

export interface SavedState<D> {
  document: D;
  version?: number;
  savedAt?: string;
}

/** « à l'instant », « il y a 3 min », sinon l'heure. */
export function savedAgo(savedAt: string, now: Date): string {
  const minutes = Math.floor((now.getTime() - new Date(savedAt).getTime()) / 60_000);
  if (minutes < 1) return 'à l’instant';
  if (minutes < 60) return `il y a ${minutes} min`;
  return `à ${new Date(savedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
}

export function useStudioDraft<D>(drafts: DraftPort<D> | undefined, draft: D, initial: SavedState<D>) {
  const [saved, setSaved] = useState<SavedState<D>>(initial);
  const [state, setState] = useState<'idle' | 'saving' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => new Date());
  const latest = useRef(draft);
  latest.current = draft;
  const savedRef = useRef(saved);
  savedRef.current = saved;
  const inflight = useRef(false);
  const unsaved = drafts !== undefined && !Object.is(draft, saved.document);

  const saveNow = useCallback(async (): Promise<void> => {
    if (drafts === undefined || inflight.current) return;
    const document = latest.current;
    if (Object.is(document, savedRef.current.document)) return;
    inflight.current = true;
    setState('saving');
    try {
      const result = await drafts.save(document, savedRef.current.version);
      setSaved({ document, version: result.version, savedAt: result.savedAt });
      setState('idle');
      setError(null);
    } catch (failure) {
      setState('error');
      setError(failure instanceof ApiError || failure instanceof Error ? failure.message : String(failure));
    } finally {
      inflight.current = false;
    }
  }, [drafts]);

  // Enregistrement automatique : réarmé à chaque modification, et après chaque enregistrement.
  useEffect(() => {
    if (!unsaved || state === 'error') return undefined;
    const timer = window.setTimeout(() => void saveNow(), AUTOSAVE_MS);
    return () => window.clearTimeout(timer);
  }, [draft, unsaved, saved, state, saveNow]);

  // « Enregistré il y a 2 min » se met à jour.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const status = drafts === undefined ? null : state === 'saving' ? 'Enregistrement…' : state === 'error' ? `Échec de l’enregistrement : ${error ?? ''}` : unsaved ? 'Modifications non enregistrées' : saved.savedAt !== undefined ? `Enregistré ${savedAgo(saved.savedAt, now)}` : 'Aucune modification';

  /** Après une publication : le brouillon n'existe plus, le document publié est l'état de référence. */
  const reset = useCallback((document: D): void => setSaved({ document }), []);

  return { unsaved, status, saveNow, reset, state, retry: () => { setState('idle'); void saveNow(); } };
}
